import Config from '@config';
import { CACHE_EXPIRATION_TIME } from '@gasbi/shared/common/features/constants';
import {
  FiltersGrid,
  Pagination,
  infiniteQueryOptions,
  normalizeInfiniteQueryData,
} from '@gasbi/shared/index';
import { createApi } from '@reduxjs/toolkit/query/react';

import axiosBaseQuery from '@/utils/axiosBaseQuery';

import {
  CapacitySettings,
  CapacitySettingsRequestBody,
  CapacitySettingsSlim,
} from './types';

export const capacitySettingsApi = createApi({
  reducerPath: 'capacitySettingsApi',
  baseQuery: axiosBaseQuery({
    baseUrl: `${Config.VITE_API_URL}/capacity-settings`,
  }),
  keepUnusedDataFor: CACHE_EXPIRATION_TIME,
  tagTypes: ['CapacitySettings', 'CapacitySettingsList'],
  endpoints: builder => ({
    getCapacitySettingsI: builder.infiniteQuery<
      CapacitySettingsSlim[],
      FiltersGrid,
      Pagination
    >({
      infiniteQueryOptions,
      query: ({ queryArg, pageParam }) => ({
        url: `/`,
        params: { ...queryArg, ...pageParam },
      }),
      providesTags: () => ['CapacitySettings', 'CapacitySettingsList'],
    }),
    getCapacitySettingsById: builder.query<CapacitySettings, string>({
      query: id => ({
        url: `/${id}`,
      }),
      providesTags: () => ['CapacitySettings'],
    }),
    createCapacitySettings: builder.mutation<
      CapacitySettings,
      CapacitySettingsRequestBody
    >({
      query: data => ({
        url: '/',
        method: 'POST',
        data,
      }),
      invalidatesTags: result => [
        'CapacitySettingsList',
        { type: 'CapacitySettings', id: result?.id },
      ],
    }),
    updateCapacitySettings: builder.mutation<
      CapacitySettings,
      CapacitySettingsRequestBody
    >({
      query: data => ({
        url: `/${data.id}`,
        method: 'PUT',
        data,
      }),
      invalidatesTags: result => [
        'CapacitySettingsList',
        { type: 'CapacitySettings', id: result?.id },
      ],
    }),
  }),
});

export const {
  useGetCapacitySettingsByIdQuery,
  useCreateCapacitySettingsMutation,
  useUpdateCapacitySettingsMutation,
} = capacitySettingsApi;

export const useGetCapacitySettingsIInfiniteQuery = normalizeInfiniteQueryData(
  capacitySettingsApi.useGetCapacitySettingsIInfiniteQuery,
);
